/**
 * Temporary Dialog shim using Radix primitives directly.
 *
 * This exists because SquareKit bundles its own React instance, which breaks
 * createPortal in isolated React roots (like StaffMessageWidget).
 *
 * TO MIGRATE: delete this file and LocalDialog.module.css, then update the
 * import in StaffMessageWidget.tsx to:
 *   import { Dialog, DialogContent, DialogHeader, DialogTitle }
 *     from '@parentsquare/squarekit/components/ui/dialog'
 * and keep the backdrop-only outside dismissal from DialogContent below.
 */
import React, { forwardRef, useRef, type HTMLAttributes, type ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import styles from './LocalDialog.module.css';

const Dialog = RadixDialog.Root;
const DialogTrigger = RadixDialog.Trigger;

const DialogContent = forwardRef<
    React.ElementRef<typeof RadixDialog.Content>,
    React.ComponentPropsWithoutRef<typeof RadixDialog.Content>
>(({ children, onInteractOutside, ...props }, ref) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    // Password managers render autofill menus outside the dialog, so clicking or focusing one
    // must not dismiss it. Only a backdrop click closes the dialog from outside.
    const handleInteractOutside: NonNullable<typeof onInteractOutside> = (event) => {
        if (event.target !== overlayRef.current) event.preventDefault();
        onInteractOutside?.(event);
    };
    return (
        <RadixDialog.Portal>
            <RadixDialog.Overlay ref={overlayRef} className={styles.overlay} />
            <RadixDialog.Content ref={ref} className={styles.content} onInteractOutside={handleInteractOutside} {...props}>
                {children}
            </RadixDialog.Content>
        </RadixDialog.Portal>
    );
});
DialogContent.displayName = 'DialogContent';

const DialogHeader = ({
    children,
    onClose,
    ...props
}: HTMLAttributes<HTMLDivElement> & { onClose?: () => void }) => (
    <div className={styles.header} {...props}>
        {children}
        {onClose && (
            <RadixDialog.Close asChild>
                <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                    <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                </button>
            </RadixDialog.Close>
        )}
    </div>
);

const DialogTitle = ({ children, ...props }: { children: ReactNode }) => (
    <RadixDialog.Title className={styles.title} {...props}>
        {children}
    </RadixDialog.Title>
);

export { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger };
